import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Breadcrumb } from 'components/breadcrumbs';
import messages from 'messages';
import { GrowthTrackerModalView } from './modals/growth-tracker-modal-view';
import { ROUTE_PERSONS_ENTITY } from 'routes/RouteList';
import { StoreContext } from 'index';
import { useHistory } from 'react-router-dom';
import Loader from 'components/loader/Loader';
import { Panel } from 'components/panel';
import Modal from 'components/modal/Modal';
import SidePanelChartCard from 'containers/partials/cards/SidePanelChartCard';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import useFetchAndFilterEntities from 'hooks/queries/useFetchAndFilterEntities';
import GrowthMainModal from 'containers/pages/growthtracker/modals/GrowthMainModal';
import GrowthPanelHeader from 'containers/pages/growthtracker/GrowthPanelHeader';
import GrowthPanel from 'containers/pages/growthtracker/GrowthPanel';
import GrowthAlertCardBody from 'containers/partials/cards/GrowthAlertCardBody';
import { useGrowthPanelContext } from 'contexts/GrowthPanelContext';

const GroupGrowth = ({ groupId }) => {
  const {
    authStore: { user, entity }
  } = useContext(StoreContext);

  const intl = useIntl();
  const { push } = useHistory();

  const group = useEntityContext(s => s.group);

  const {
    loading: loadingGroups,
    error,
    fetchEntities
  } = useFetchAndFilterEntities({ entityId: groupId });

  const {
    loading: loadingResults,
    sidePanelData,
    onCloseSidePanel,
    error: resultsError
  } = useGrowthPanelContext();

  const [modalData, setModalData] = useState(null);
  const [alertData, setAlertData] = useState(null);

  useEffect(() => {
    if (groupId) {
      fetchEntities({ entityId: groupId });
    }
  }, [groupId]);

  useEffect(() => {
    if (!loadingGroups && group?.clients?.length === 0 && group.id) {
      setAlertData({
        dismissHandler: () => onCloseAlert(),
        okHandler: () =>
          push(ROUTE_PERSONS_ENTITY.replace(':entityId', groupId)),
        okLabel: intl.formatMessage(messages.benchmarksAlertButtonGoToSporters),
        dismissLabel: intl.formatMessage(messages.benchmarksAlertButtonGoBack),
        message: intl.formatMessage(messages.benchmarksAlertMessageEmptyGroup)
      });
    } else {
      setAlertData(null);
    }
  }, [loadingGroups, group.clients, group.id]);

  useEffect(() => {
    if (error) {
      setAlertData({
        dismissHandler: () => setAlertData(null),
        okHandler: () => setAlertData(null),
        okLabel: intl.formatMessage(messages.benchmarksAlertButtonNoBenchmark),
        message: intl.formatMessage(messages.benchmarksAlertMessageNoBenchmark)
      });
    }
  }, [error]);

  useEffect(() => {
    if (resultsError) {
      if (resultsError === 'Timeout') {
        setAlertData({
          dismissButton: false,
          okHandler: null,
          message: intl.formatMessage(messages.benchmarksAlertMessageTimeout)
        });
      } else {
        setAlertData({
          dismissButton: false,
          okHandler: () => onCloseAlert(null),
          okLabel: intl.formatMessage(messages.benchmarksAlertButtonError),
          message: intl.formatMessage(messages.benchmarksAlertMessageError)
        });
      }
    }
  }, [resultsError]);

  useEffect(() => {
    if (loadingResults === false) {
      setAlertData(null);
    }
  }, [loadingResults]);

  const toggleSelectGroupModal = (view, isMaster, submitHandler) => {
    if (modalData) {
      setModalData(null);
    } else {
      setModalData({
        view: view ? null : view,
        modalMaster: isMaster,
        submitHandler: view ? null : submitHandler
      });
    }
  };

  const onCloseAlert = () => {
    setAlertData(null);
    toggleSelectGroupModal(GrowthTrackerModalView.GROUP, true);
  };

  if (!group) return <Loader />;

  return (
    <>
      {modalData && (
        <GrowthMainModal
          entityId={user.rootEntityId}
          onCloseHandler={() => {
            setModalData(null);
          }}
          view={modalData.view}
          master={modalData.modalMaster}
          onSubmitHandler={modalData.submitHandler}
        />
      )}
      {loadingResults && <Loader />}
      <Panel refactoringResults>
        {group?.id && (
          <GrowthPanelHeader
            breadcrumbs={getBreadCrumbs(
              toggleSelectGroupModal,
              group,
              user,
              entity
            )}
          />
        )}
        <GrowthPanel />
      </Panel>
      {alertData && (
        <Modal
          card
          benchmarksTableAlert
          noCloseButton
          isOpen={true}
          onClose={onCloseAlert}
        >
          <GrowthAlertCardBody alertData={alertData} />
        </Modal>
      )}
      {sidePanelData && (
        <Modal cardPanel isOpen={true} onClose={onCloseSidePanel}>
          <SidePanelChartCard {...sidePanelData} />
        </Modal>
      )}
    </>
  );
};

export default GroupGrowth;

const getBreadCrumbs = (gotoHandler, group, user, entity) => {
  return (
    <>
      <Breadcrumb noLink>
        <FormattedMessage {...messages.benchmarksBreadcrumbGroups} />
      </Breadcrumb>
      <Breadcrumb
        onClick={() => gotoHandler(GrowthTrackerModalView.GROUP, true)}
      >
        {user.getEntityName(entity)}
      </Breadcrumb>
      <Breadcrumb
        onClick={() => gotoHandler(GrowthTrackerModalView.GROUP, true)}
      >
        {group.name}
      </Breadcrumb>
    </>
  );
};
